/**
 * OverviewSections — Scrollable page body for the monitoring surface.
 *
 * Renders below the map hero when in overview (global) mode:
 * 1. Signal summary bar — aggregate counts at a glance
 * 2. Signal feed — browsable, expandable signal list
 * 3. Suggestions — investigation candidates
 */

import { ChevronDown, ChevronRight, Sparkles, Radio, TriangleAlert, Layers, MapPin } from 'lucide-react';
import { useState, useCallback } from 'react';
import type { WorldMonitorSignal } from '../../api/worldMonitor';
import type { InvestigationSuggestion } from '../../api/osint';
import type { Mode } from '../../hooks/useWorkspaceState';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { SummaryModules, type ModuleData } from './SummaryModules';
import { InvestigationsMode } from './InvestigationsMode';
import { RegionalStream, STREAM_REGIONS } from './RegionalStream';
import { DomainStream, MODE_DOMAIN_STREAMS } from './DomainStream';
import type { SignalSummaryResponse, OsintHealthResponse } from '../../api/osint';
import type { InvestigationSummary } from '../../types/investigation';
import type { CrossTheatreParadox } from '../../api/crossTheatreParadox';

// ── Types ──────────────────────────────────────────────────────────

interface OverviewSectionsProps {
  signals: WorldMonitorSignal[];
  signalCount: number;
  criticalCount: number;
  significantCount: number;
  updatedAt?: string;
  investigationCount: number;
  convergenceCount: number;
  suggestions: InvestigationSuggestion[];
  suggestionsLoading: boolean;
  onPromoteSuggestion?: (suggestion: InvestigationSuggestion) => void;
  promotingSuggestionId?: string | null;
  onInvestigateSignal?: (signal: WorldMonitorSignal) => void;
  investigatingSignalId?: string | null;
  activeMode: Mode;
  // Investigations mode
  investigations: InvestigationSummary[];
  investigationsLoading: boolean;
  onSelectInvestigation: (id: string) => void;
  selectedInvestigationId: string | null;
  // Additional data for summary modules
  summary?: SignalSummaryResponse;
  health?: OsintHealthResponse;
  categoryCounts: Record<string, number>;
  sourceCounts: Record<string, number>;
  severityCounts: Record<string, number>;
  // Paradox data
  localParadoxCount: number;
  localParadoxCriticalCount: number;
  crossTheatreParadoxes: CrossTheatreParadox[];
  isSectionVisible: (id: string) => boolean;
  moduleOrder?: string[];
}

// ── Helpers ─────────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

function severityDotColor(severity: string): string {
  switch (severity.toLowerCase()) {
    case 'critical': return 'bg-[var(--e-red-400)]';
    case 'high': return 'bg-[var(--e-amber-400)]';
    case 'moderate': return 'bg-[var(--e-purple-400)]';
    default: return 'bg-[var(--e-text-muted)]/40';
  }
}

function categoryColor(category: string): string {
  if (category.includes('finance') || category.includes('market')) return 'text-emerald-400';
  if (category.includes('maritime') || category.includes('trade')) return 'text-cyan-400';
  if (category.includes('geopolitical') || category.includes('conflict')) return 'text-red-400';
  if (category.includes('cyber') || category.includes('infrastructure')) return 'text-blue-400';
  if (category.includes('aviation') || category.includes('military')) return 'text-sky-400';
  if (category.includes('natural') || category.includes('disaster')) return 'text-orange-400';
  return 'text-[var(--e-text-muted)]';
}

// ── Signal Row ──────────────────────────────────────────────────────

function SignalRow({
  signal,
  onInvestigate,
  investigating,
}: {
  signal: WorldMonitorSignal;
  onInvestigate?: (signal: WorldMonitorSignal) => void;
  investigating: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[var(--e-bg-hover)] transition-colors"
      >
        {expanded
          ? <ChevronDown className="w-3.5 h-3.5 text-[var(--e-text-muted)] flex-shrink-0" />
          : <ChevronRight className="w-3.5 h-3.5 text-[var(--e-text-muted)] flex-shrink-0" />}
        <span className={cn('w-2 h-2 rounded-full flex-shrink-0', severityDotColor(signal.severity))} />
        <span className="text-xs font-medium text-[var(--e-text-primary)] truncate flex-1">
          {signal.title}
        </span>
        <Badge variant="outline" className={cn('rounded-full text-[10px] font-mono py-0 h-5', categoryColor(signal.category))}>
          {signal.category.replace(/_/g, ' ')}
        </Badge>
        <span className="text-[10px] font-mono text-[var(--e-text-muted)] flex-shrink-0">
          {signal.source}
        </span>
        <span className="text-[10px] font-mono text-[var(--e-text-muted)] flex-shrink-0 w-12 text-right">
          {relativeTime(signal.timestamp)}
        </span>
      </button>

      {expanded && (
        <div className="px-4 pb-3 pt-1 ml-6 space-y-2">
          <p className="text-[11px] text-[var(--e-text-secondary)] leading-relaxed">
            {signal.description}
          </p>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-[10px] font-mono">
            <div><span className="text-[var(--e-text-muted)]">severity</span>{' '}<span className="text-[var(--e-text-primary)]">{signal.severity}</span></div>
            <div><span className="text-[var(--e-text-muted)]">confidence</span>{' '}<span className="text-[var(--e-text-primary)]">{signal.confidence.toFixed(2)}</span></div>
            <div><span className="text-[var(--e-text-muted)]">region</span>{' '}<span className="text-[var(--e-text-primary)]">{signal.region}</span></div>
            <div><span className="text-[var(--e-text-muted)]">geo</span>{' '}<span className="text-[var(--e-text-primary)]">{signal.geo.lat.toFixed(2)}, {signal.geo.lon.toFixed(2)}</span></div>
          </div>
          {onInvestigate && !signal.investigation_id && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onInvestigate(signal); }}
              disabled={investigating}
              className="mt-1 h-7 text-[11px] font-semibold rounded-md
                bg-[var(--e-purple-400)]/10 text-[var(--e-purple-400)]
                border-[var(--e-purple-400)]/20
                hover:bg-[var(--e-purple-400)]/20"
            >
              {investigating ? '...' : '→ Investigate'}
            </Button>
          )}
          {signal.investigation_id && (
            <Badge variant="outline" className="text-[10px] font-mono text-[var(--e-purple-400)] border-[var(--e-purple-400)]/20">
              linked to investigation
            </Badge>
          )}
        </div>
      )}
      <Separator className="last:hidden" />
    </div>
  );
}

// ── Suggestion Card ─────────────────────────────────────────────────

export function SuggestionCard({
  suggestion,
  onPromote,
  promoting,
}: {
  suggestion: InvestigationSuggestion;
  onPromote?: (s: InvestigationSuggestion) => void;
  promoting: boolean;
}) {
  const typeLabel = suggestion.suggestion_type === 'paradox_investigation' ? 'Paradox'
    : suggestion.suggestion_type === 'forge_opportunity' ? 'FORGE'
    : 'Cluster';
  const typeColor = suggestion.suggestion_type === 'paradox_investigation' ? 'text-[var(--e-red-400)] border-[var(--e-red-400)]/20'
    : suggestion.suggestion_type === 'forge_opportunity' ? 'text-[var(--e-orange-400)] border-[var(--e-orange-400)]/20'
    : 'text-[var(--e-amber-400)] border-[var(--e-amber-400)]/20';

  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={cn('rounded-full text-[10px] font-mono font-semibold py-0 h-5', typeColor)}>
            {typeLabel}
          </Badge>
          <span className="text-[10px] font-mono text-[var(--e-text-muted)]">
            {suggestion.signal_count} signals
          </span>
        </div>
        <p className="text-xs font-medium text-[var(--e-text-primary)] mt-0.5 truncate">
          {suggestion.title}
        </p>
        <p className="text-[10px] text-[var(--e-text-muted)] mt-0.5 line-clamp-2">
          {suggestion.reason}
        </p>
        {/* Enriched context from backend (048) */}
        {(() => {
          const meta = suggestion.metadata ?? {};
          const titles = Array.isArray(meta.representative_titles) ? meta.representative_titles as string[] : [];
          const breakdown = typeof meta.severity_breakdown === 'object' && meta.severity_breakdown
            ? meta.severity_breakdown as Record<string, number> : null;
          return (
            <>
              {titles.length > 0 && (
                <div className="mt-1 space-y-0.5">
                  {titles.slice(0, 2).map((t, i) => (
                    <p key={i} className="text-[10px] text-[var(--e-text-secondary)] truncate">· {t}</p>
                  ))}
                </div>
              )}
              {breakdown && (
                <div className="flex gap-2 mt-1 text-[9px] font-mono text-[var(--e-text-muted)]">
                  {Object.entries(breakdown).filter(([, c]) => c > 0).map(([level, count]) => (
                    <span key={level} className={
                      level === 'critical' ? 'text-[var(--e-red-400)]' :
                      level === 'significant' ? 'text-[var(--e-amber-400)]' : ''
                    }>{count} {level}</span>
                  ))}
                </div>
              )}
            </>
          );
        })()}
      </div>
      {onPromote && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPromote(suggestion)}
          disabled={promoting}
          className="flex-shrink-0 h-7 text-[10px] font-semibold rounded-md
            bg-[var(--e-purple-400)]/10 text-[var(--e-purple-400)]
            border-[var(--e-purple-400)]/20
            hover:bg-[var(--e-purple-400)]/20"
        >
          {promoting ? '...' : 'Promote'}
        </Button>
      )}
      <Separator className="last:hidden" />
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────

export function OverviewSections({
  signals,
  signalCount,
  criticalCount,
  significantCount,
  updatedAt,
  investigationCount,
  convergenceCount,
  suggestions,
  onPromoteSuggestion,
  promotingSuggestionId,
  onInvestigateSignal,
  investigatingSignalId,
  activeMode,
  investigations,
  investigationsLoading,
  onSelectInvestigation,
  selectedInvestigationId,
  summary,
  health,
  categoryCounts,
  sourceCounts,
  severityCounts,
  localParadoxCount,
  localParadoxCriticalCount,
  crossTheatreParadoxes,
  isSectionVisible,
  moduleOrder: _moduleOrder,
}: OverviewSectionsProps) {
  const moduleData: ModuleData = {
    signals,
    summary,
    health,
    categoryCounts: categoryCounts ?? {},
    sourceCounts: sourceCounts ?? {},
    severityCounts: severityCounts ?? {},
    investigationCount,
    convergenceCount,
    localParadoxCount,
    localParadoxCriticalCount,
    crossTheatreParadoxes,
  };
  const [signalFeedExpanded, setSignalFeedExpanded] = useState(true);
  const [suggestionsExpanded, setSuggestionsExpanded] = useState(true);

  const handleToggleSignalFeed = useCallback(() => {
    setSignalFeedExpanded((prev) => !prev);
  }, []);

  const handleToggleSuggestions = useCallback(() => {
    setSuggestionsExpanded((prev) => !prev);
  }, []);

  // ── Investigations mode gets its own discovery surface ──
  if (activeMode === 'investigations') {
    return (
      <div className="bg-[var(--e-bg-app)] border-t border-[var(--e-border-primary)]">
        <InvestigationsMode
          investigations={investigations}
          isLoading={investigationsLoading}
          onSelectInvestigation={onSelectInvestigation}
          selectedInvestigationId={selectedInvestigationId}
        />
      </div>
    );
  }

  return (
    <div className="bg-[var(--e-bg-app)]">
      {/* ── Signal summary bar (anchored, not in grid) ── */}
      <div data-section-id="signal-summary" className="flex items-center gap-4 px-6 py-2.5 bg-[var(--e-bg-card)] border-b border-[var(--e-border-secondary)]">
        <div className="flex items-center gap-1.5 text-xs font-mono text-[var(--e-text-secondary)]">
          <Radio className="w-3.5 h-3.5" />
          <span className="font-semibold text-[var(--e-text-primary)]">{signalCount}</span> signals
        </div>
        {criticalCount > 0 && (
          <Badge variant="outline" className="rounded-full font-mono text-[10px] py-0 h-5 text-[var(--e-red-400)] border-[var(--e-red-400)]/20 bg-[var(--e-red-400)]/5">
            <TriangleAlert className="w-3 h-3" />
            {criticalCount} critical
          </Badge>
        )}
        {significantCount > 0 && (
          <Badge variant="outline" className="rounded-full font-mono text-[10px] py-0 h-5 text-[var(--e-amber-400)] border-[var(--e-amber-400)]/20 bg-[var(--e-amber-400)]/5">
            {significantCount} significant
          </Badge>
        )}
        <div className="flex items-center gap-1.5 text-xs font-mono text-[var(--e-text-muted)]">
          <Layers className="w-3.5 h-3.5" />
          <span>{investigationCount}</span> investigations
        </div>
        {convergenceCount > 0 && (
          <div className="flex items-center gap-1.5 text-xs font-mono text-[var(--e-text-muted)]">
            <MapPin className="w-3.5 h-3.5" />
            <span>{convergenceCount}</span> hotspots
          </div>
        )}
        <div className="ml-auto text-[10px] font-mono text-[var(--e-text-muted)]">
          {updatedAt ? relativeTime(updatedAt) : ''}
        </div>
      </div>

      {/* ── Widget canvas grid ── */}
      <div className="grid grid-cols-1 md:grid-cols-6 xl:grid-cols-12 gap-3 px-4 py-4">

        {/* Summary modules (S-sized widgets) */}
        {isSectionVisible('summary-modules') && (
          <div className="col-span-1 md:col-span-6 xl:col-span-12">
            <SummaryModules activeMode={activeMode} data={moduleData} />
          </div>
        )}

        {/* Regional stream widgets (M-sized) */}
        {Object.keys(STREAM_REGIONS).map((streamId) =>
          isSectionVisible(streamId) ? (
            <div key={streamId} className="col-span-1 md:col-span-3 xl:col-span-6">
              <RegionalStream
                streamId={streamId}
                signals={signals}
                onInvestigateSignal={onInvestigateSignal}
              />
            </div>
          ) : null,
        )}

        {/* Domain stream widgets — per-mode defaults, operator-hideable (M-sized) */}
        {(MODE_DOMAIN_STREAMS[activeMode] ?? []).map((streamId) =>
          isSectionVisible(streamId) ? (
            <div key={streamId} className="col-span-1 md:col-span-3 xl:col-span-6">
              <DomainStream
                streamId={streamId}
                signals={signals}
                onInvestigateSignal={onInvestigateSignal}
              />
            </div>
          ) : null,
        )}

      {/* Signal feed widget (L-sized) */}
      {isSectionVisible('signal-feed') && <div data-section-id="signal-feed" className="col-span-1 md:col-span-6 xl:col-span-8 rounded-lg border border-[var(--e-border-secondary)] bg-[var(--e-bg-card)]">
        <Button
          variant="ghost"
          onClick={handleToggleSignalFeed}
          className="w-full flex items-center justify-start gap-2 px-6 py-2.5 h-auto rounded-none"
        >
          {signalFeedExpanded
            ? <ChevronDown className="w-3.5 h-3.5 text-[var(--e-text-muted)]" />
            : <ChevronRight className="w-3.5 h-3.5 text-[var(--e-text-muted)]" />}
          <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--e-text-muted)]">
            Live signals
          </span>
          <Badge variant="secondary" className="rounded-full text-[10px] font-mono py-0 h-5">
            {signals.length}
          </Badge>
        </Button>

        {signalFeedExpanded && (
          <div className="max-h-[28rem] overflow-y-auto">
            {signals.length === 0 ? (
              <div className="px-6 py-8 text-center text-xs text-[var(--e-text-muted)]">
                No signals match the current mode and scope.
              </div>
            ) : (
              signals.slice(0, 200).map((signal) => (
                <SignalRow
                  key={signal.id}
                  signal={signal}
                  onInvestigate={onInvestigateSignal}
                  investigating={investigatingSignalId === signal.id}
                />
              ))
            )}
          </div>
        )}
      </div>}

      {/* Suggestions widget (M-sized) */}
      {isSectionVisible('suggestions') && suggestions.length > 0 && (
        <div data-section-id="suggestions" className="col-span-1 md:col-span-3 xl:col-span-4 rounded-lg border border-[var(--e-border-secondary)]/50 bg-[var(--e-bg-card)]">
          <Button
            variant="ghost"
            onClick={handleToggleSuggestions}
            className="w-full flex items-center justify-start gap-2 px-6 py-2.5 h-auto rounded-none"
          >
            {suggestionsExpanded
              ? <ChevronDown className="w-3.5 h-3.5 text-[var(--e-text-muted)]" />
              : <ChevronRight className="w-3.5 h-3.5 text-[var(--e-text-muted)]" />}
            <Sparkles className="w-3.5 h-3.5 text-[var(--e-purple-400)]" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--e-text-muted)]">
              Suggestions
            </span>
            <Badge variant="outline" className="rounded-full text-[10px] font-mono py-0 h-5
              text-[var(--e-purple-400)] border-[var(--e-purple-400)]/20">
              {suggestions.length}
            </Badge>
          </Button>

          {suggestionsExpanded && (
            <div className="max-h-[16rem] overflow-y-auto">
              {suggestions.map((suggestion) => (
                <SuggestionCard
                  key={suggestion.suggestion_id}
                  suggestion={suggestion}
                  onPromote={onPromoteSuggestion}
                  promoting={promotingSuggestionId === suggestion.suggestion_id}
                />
              ))}
            </div>
          )}
        </div>
      )}

      </div>{/* end widget canvas grid */}
    </div>
  );
}
